import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@redux/hooks';

import { CandidateSuccessModal, FormDialog } from '@components';
import { CandidateRejectionReasons } from './components/CandidateRejectionReasons';
import {
  ScheduleInterviewFields,
  ScheduleInterviewNote,
} from './components/ScheduleInterviewFields';
import { ResolutionBlock } from './components/ResolutionBlock';
import { Stack, TextField } from '@mui/material';

import {
  candidateSubtitle,
  isStatusLoading,
  normalizeCandidateStatus,
} from '@utils';
import { validationSchema } from './validationSchema';

import {
  candidateDetailsSelectors,
  provideCandidateFeedback,
  provideClientInterviewFeedback,
  setCandidateDetailsAction,
} from '@redux/candidateDetails';

import { CandidateFeedbackFormValues } from '@types';
import {
  CandidateDetailsAction,
  CandidateStatus,
  ClientFeedbackResolution,
  SearchParams,
} from '@constants';
import { getUserOffset } from '@utils';
import { logEvent } from '@services';

const RESOLUTION_BY_ACTION: Partial<
  Record<CandidateDetailsAction, ClientFeedbackResolution>
> = {
  [CandidateDetailsAction.APPROVE_CANDIDATE]:
    ClientFeedbackResolution.HiringApproved,
  [CandidateDetailsAction.CLIENT_INTERVIEW_PASSED]:
    ClientFeedbackResolution.HiringApproved,
  [CandidateDetailsAction.SCHEDULE_INTERVIEW]:
    ClientFeedbackResolution.CallRequested,
  [CandidateDetailsAction.SCHEDULE_NEXT_ROUND]:
    ClientFeedbackResolution.CallRequested,
  [CandidateDetailsAction.SKIP_CANDIDATE]: ClientFeedbackResolution.Rejected,
  [CandidateDetailsAction.CLIENT_INTERVIEW_FAILED]:
    ClientFeedbackResolution.Rejected,
};

export const ClientFeedbackModal = () => {
  const dispatch = useAppDispatch();
  const [, setSearchParams] = useSearchParams();

  const {
    provideClientFeedbackAPIStatus,
    provideClientInterviewFeedbackAPIStatus,
    writeError,
    data,
  } = useAppSelector(candidateDetailsSelectors.getCandidateDetailsData);
  const candidateDetailsAction = useAppSelector(
    candidateDetailsSelectors.getCandidateDetailsAction,
  );

  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState<boolean>(false);
  const candidateStatus = normalizeCandidateStatus(data?.status);

  const latestCI = data?.interview?.at(-1) || null;
  const isRejection = [
    CandidateDetailsAction.SKIP_CANDIDATE,
    CandidateDetailsAction.CLIENT_INTERVIEW_FAILED,
  ].includes(candidateDetailsAction!);
  const isNextRound =
    candidateDetailsAction === CandidateDetailsAction.SCHEDULE_NEXT_ROUND;
  const isScheduling =
    candidateDetailsAction === CandidateDetailsAction.SCHEDULE_INTERVIEW ||
    isNextRound;
  const candidateName = [data?.profile?.firstName, data?.profile?.lastName]
    .filter(Boolean)
    .join(' ');
  const header = {
    label: isNextRound
      ? 'Schedule next round'
      : isScheduling
        ? 'Schedule interview'
        : candidateName,
    subLabel: isScheduling || !data ? candidateName : candidateSubtitle(data),
  };
  const [successHeader, setSuccessHeader] = useState(header);

  const isLoading =
    isStatusLoading(provideClientFeedbackAPIStatus) ||
    isStatusLoading(provideClientInterviewFeedbackAPIStatus);

  const formik = useFormik<CandidateFeedbackFormValues>({
    initialValues: {
      resolution: null,
      rejectionReasons: null,
      anotherRoundReason: null,
      interviewSlots: null,
      interviewOption: null,
      clientCalendar: null,
      details: null,
      clientTimezone: latestCI?.clientTimezone || getUserOffset(),
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      if (data?.id) {
        const res = [
          CandidateDetailsAction.APPROVE_CANDIDATE,
          CandidateDetailsAction.SCHEDULE_INTERVIEW,
          CandidateDetailsAction.SKIP_CANDIDATE,
        ].includes(candidateDetailsAction!)
          ? await dispatch(
              provideCandidateFeedback({
                candidateId: data.id,
                resolution: values.resolution!,
                rejectionReasons: values.rejectionReasons,
                interviewSlots: values.interviewSlots,
                clientCalendar: values.clientCalendar,
                details: values.details,
                clientTimezone: values.clientTimezone,
              }),
            )
          : await dispatch(
              provideClientInterviewFeedback({
                candidateId: data.id,
                resolution: values.resolution!,
                rejectionReasons: values.rejectionReasons,
                interviewSlots: values.interviewSlots,
                clientCalendar: values.clientCalendar,
                details: isNextRound
                  ? [values.anotherRoundReason, values.details]
                      .filter(Boolean)
                      .join(' — ')
                  : values.details,
                clientTimezone: values.clientTimezone,
              }),
            );

        if (
          provideCandidateFeedback.fulfilled.match(res) ||
          provideClientInterviewFeedback.fulfilled.match(res)
        ) {
          handleModalClose();
          setSuccessHeader(header);
          setSuccessModalOpen(true);
        }

        if (values.resolution === ClientFeedbackResolution.Rejected) {
          logEvent('candidate_rejection_submitted');
        }
        if (values.resolution === ClientFeedbackResolution.CallRequested) {
          logEvent('interview_submitted');
        }
        if (values.resolution === ClientFeedbackResolution.HiringApproved) {
          logEvent('candidate_hired');
        }
      }
    },
  });

  useEffect(() => {
    if (candidateDetailsAction) {
      if (
        [
          CandidateDetailsAction.APPROVE_CANDIDATE,
          CandidateDetailsAction.SCHEDULE_INTERVIEW,
          CandidateDetailsAction.SCHEDULE_NEXT_ROUND,
          CandidateDetailsAction.SKIP_CANDIDATE,
          CandidateDetailsAction.CLIENT_INTERVIEW_PASSED,
          CandidateDetailsAction.CLIENT_INTERVIEW_FAILED,
        ].includes(candidateDetailsAction)
      ) {
        formik.setFieldValue(
          'resolution',
          RESOLUTION_BY_ACTION[candidateDetailsAction] ?? null,
        );
        setModalOpen(true);
      }
    } else {
      formik.resetForm();
      setModalOpen(false);
    }
  }, [candidateDetailsAction]);

  const handleModalClose = () => {
    dispatch(setCandidateDetailsAction(null));
    setSearchParams(
      (_searchParams) => {
        _searchParams.delete(SearchParams.ACTION);
        return _searchParams;
      },
      { replace: true },
    );
  };

  const detailsError =
    ((formik.submitCount || formik.touched.details) && formik.errors.details) ||
    '';

  const missing = isScheduling
    ? isNextRound && !formik.values.anotherRoundReason
      ? 'Say what this round should settle to submit.'
      : !formik.values.interviewSlots?.some((slot) => slot.start && slot.end) &&
          !formik.values.clientCalendar
        ? 'Pick a time or share your calendar to submit.'
        : ''
    : isRejection && !formik.values.rejectionReasons?.length
      ? 'Pick a reason to submit.'
      : isRejection && !formik.values.details?.trim()
        ? 'Say what the candidate was missing to submit.'
        : '';

  if (!data) return null;

  return (
    <>
      <FormDialog
        isOpen={isModalOpen}
        onClose={handleModalClose}
        {...header}
        onSubmit={formik.handleSubmit}
        isLoading={isLoading}
        submitDisabled={Boolean(missing)}
        submitBtnLabel={isScheduling ? 'Request this slot' : undefined}
        hint={missing}
        error={writeError}
      >
        <Stack gap="1.5rem">
          <ResolutionBlock candidateDetailsAction={candidateDetailsAction} />
          {isRejection && <CandidateRejectionReasons formik={formik} />}
          {isScheduling && (
            <ScheduleInterviewFields
              candidateDetails={data}
              formik={formik}
              isNextRound={isNextRound}
            />
          )}
          <TextField
            fullWidth
            multiline
            minRows={3}
            required={isRejection}
            name="details"
            label={
              isNextRound
                ? 'Your feedback on the last round'
                : isScheduling
                  ? 'Anything else'
                  : 'Notes'
            }
            placeholder={
              isRejection
                ? 'What was the candidate missing? This is what improves the next submission.'
                : isNextRound
                  ? 'How did the round go, and what is still open?'
                  : isScheduling
                    ? 'Who should attend, what you are waiting on, anything the coordinator needs'
                    : 'Please add any suggestions you have about this candidate or the interview.'
            }
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            value={formik.values.details || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            helperText={detailsError || undefined}
            error={!!detailsError}
          />
          {isScheduling && <ScheduleInterviewNote />}
        </Stack>
      </FormDialog>
      <CandidateSuccessModal
        header={successHeader}
        isOpen={isSuccessModalOpen}
        isPassed={candidateStatus === CandidateStatus.CallRequested}
        label={
          candidateStatus === CandidateStatus.CallRequested
            ? "Thanks, we'll set up an interview"
            : 'Thank you for your response!'
        }
        subLabel={'Our Recruiter may get back to you to clarify details.'}
        handleClose={() => setSuccessModalOpen(false)}
      />
    </>
  );
};
